import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import api from '../../lib/request';

const NOTE_MAX_LENGTH = 2000;

const capitalizeFirstLetter = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/^(\s*\S)/, (match) => match.toUpperCase());
};

const GuideNotes = ({
  notes = [],
  stepPosition,
  stepId,
  canManage,
  projectSlug,
  guideSlug,
  onNoteCreated,
  onNoteUpdated,
  onNoteDeleted,
  mode = 'floating',
  onClose,
  focusNoteId,
  onFocusHandled
}) => {
  const [body, setBody] = useState('');
  const [isTodo, setIsTodo] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionPendingId, setActionPendingId] = useState(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [showScrollCueDown, setShowScrollCueDown] = useState(false);
  const [showScrollCueUp, setShowScrollCueUp] = useState(false);
  const listRef = useRef(null);
  const lastFocusIdRef = useRef(null);
  const panelRef = useRef(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const stepNotes = useMemo(() => {
    const filtered = (notes || []).filter((note) => {
      if (!note) return false;
      if (stepId && note.stepId) return note.stepId === stepId;
      return Number(note.stepPosition) === Number(stepPosition);
    });
    return filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [notes, stepId, stepPosition]);

  useEffect(() => {
    if (mode === 'inline') {
      setHasOverflow(false);
      setShowScrollCueDown(false);
      setShowScrollCueUp(false);
      return;
    }
    const el = listRef.current;
    if (!el) return;

    const updateOverflow = () => {
      const overflow = el.scrollHeight > el.clientHeight + 1;
      setHasOverflow(overflow);
      if (!overflow) {
        setShowScrollCueDown(false);
        setShowScrollCueUp(false);
        return;
      }
      const atTop = el.scrollTop <= 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setShowScrollCueDown(!atBottom);
      setShowScrollCueUp(atBottom && !atTop);
    };

    updateOverflow();
    const handleScroll = () => updateOverflow();
    const handleResize = () => updateOverflow();
    el.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [mode, stepNotes.length]);

  useEffect(() => {
    if (mode !== 'floating') return;
    if (focusNoteId) return;
    const el = listRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      try {
        el.scrollTop = el.scrollHeight;
      } catch (e) {}
    }, 0);
    return () => clearTimeout(timer);
  }, [mode, stepNotes.length, focusNoteId]);

  useEffect(() => {
    if (!focusNoteId || focusNoteId === lastFocusIdRef.current) {
      return;
    }
    const el = listRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      const target = el.querySelector(`[data-note-id="${focusNoteId}"]`);
      if (!target) return;
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('note-highlight');
        setTimeout(() => {
          try { target.classList.remove('note-highlight'); } catch (e) {}
        }, 1500);
        try { target.focus({ preventScroll: true }); } catch (e) {}
      } catch (e) {}
      lastFocusIdRef.current = focusNoteId;
      if (typeof onFocusHandled === 'function') {
        onFocusHandled(focusNoteId);
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [focusNoteId, onFocusHandled]);

  const handleDragMove = useCallback((event) => {
    if (!dragRef.current.active) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    setDragOffset({
      x: dragRef.current.origX + dx,
      y: dragRef.current.origY + dy
    });
  }, []);

  const handleDragEnd = useCallback(function handleDragEndCallback() {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', handleDragEndCallback);
  }, [handleDragMove]);

  useEffect(() => {
    return () => {
      dragRef.current.active = false;
      window.removeEventListener('pointermove', handleDragMove);
      window.removeEventListener('pointerup', handleDragEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  const handleDragStart = (event) => {
    if (mode !== 'floating') return;
    if (event.target && event.target.closest && event.target.closest('button')) return;
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      origX: dragOffset.x,
      origY: dragOffset.y
    };
    window.addEventListener('pointermove', handleDragMove);
    window.addEventListener('pointerup', handleDragEnd);
    event.preventDefault();
  };

  const handleConvertToTodo = async (note) => {
    if (!note?.id) return;
    if (!projectSlug || !guideSlug) return;
    setActionPendingId(note.id);
    try {
      const response = await api.post(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/notes/${note.id}/todo`);
      if (!response?.ok) {
        throw new Error(response?.message || 'Unable to update note.');
      }
      if (response.note && typeof onNoteUpdated === 'function') {
        onNoteUpdated(response.note);
      }
      toast.success('Note marked as TODO.');
    } catch (err) {
      toast.error(err?.message || 'Unable to update note.');
    } finally {
      setActionPendingId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;

    const trimmed = body.trim();
    if (!trimmed) {
      setError('Note cannot be empty.');
      return;
    }
    if (trimmed.length > NOTE_MAX_LENGTH) {
      setError(`Note must be ${NOTE_MAX_LENGTH} characters or less.`);
      return;
    }
    if (!projectSlug || !guideSlug) {
      setError('Guide not available.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const payload = {
        body: trimmed,
        stepPosition,
        ...(stepId ? { stepId } : {}),
        isTodo
      };
      const response = await api.post(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/notes`, payload);
      if (!response?.ok) {
        throw new Error(response?.message || 'Unable to add note.');
      }
      if (response.note && typeof onNoteCreated === 'function') {
        onNoteCreated(response.note);
      }
      toast.success('Note added.');
      setBody('');
      setIsTodo(false);
    } catch (err) {
      setError(err?.message || 'Unable to add note.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkDone = async (note) => {
    if (!note?.id) return;
    if (!projectSlug || !guideSlug) return;
    setActionPendingId(note.id);
    try {
      const response = await api.post(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/notes/${note.id}/done`);
      if (!response?.ok) {
        throw new Error(response?.message || 'Unable to mark TODO as done.');
      }
      if (response.note && typeof onNoteUpdated === 'function') {
        onNoteUpdated(response.note);
      }
      toast.success('TODO marked as done.');
    } catch (err) {
      toast.error(err?.message || 'Unable to mark TODO as done.');
    } finally {
      setActionPendingId(null);
    }
  };

  const handleDelete = async (note) => {
    if (!note?.id) return;
    if (!projectSlug || !guideSlug) return;
    if (!window.confirm('Delete this note?')) {
      return;
    }
    setActionPendingId(note.id);
    try {
      const response = await api.delete(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/notes/${note.id}`);
      if (!response?.ok) {
        throw new Error(response?.message || 'Unable to delete note.');
      }
      if (typeof onNoteDeleted === 'function') {
        onNoteDeleted(response.deletedId || note.id);
      }
      toast.success('Note deleted.');
    } catch (err) {
      toast.error(err?.message || 'Unable to delete note.');
    } finally {
      setActionPendingId(null);
    }
  };

  return (
    <div
      ref={panelRef}
      className={`note-panel${mode === 'inline' ? ' note-panel-inline' : ''}`}
      style={mode === 'floating' ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` } : undefined}
    >
      <div className="note-panel-header" onPointerDown={handleDragStart}>
        <div className="note-panel-title">Notes ({stepNotes.length})</div>
        {typeof onClose === 'function' ? (
          <button
            type="button"
            className="btn btn-sm btn-link note-close"
            onClick={onClose}
            aria-label="Close notes"
            style={{ textDecoration: 'none' }}
          >
            ×
          </button>
        ) : null}
      </div>
      {stepNotes.length === 0 ? <div className="note-empty">No notes yet.</div> : null}
      <div className="note-list-wrapper">
        <div className={`note-list${hasOverflow ? ' note-list-scrollable' : ''}`} ref={listRef}>
          {stepNotes.map((note) => {
            const authorName = capitalizeFirstLetter(note?.author?.name || note?.author?.username || 'User');
            const displayBody = capitalizeFirstLetter(note?.body || '');
            const isTodoNote = note?.isTodo === true;
            const isDone = isTodoNote && note?.todoStatus === 'done';
            const doneBy = note?.todoCompletedBy?.name || note?.todoCompletedBy?.username || '';
          return (
            <div
              key={note.id}
              className={`note-item${isTodoNote && !isDone ? ' todo-open' : ''}${isDone ? ' todo-done' : ''}`}
              data-note-todo={isTodoNote && !isDone ? 'open' : isTodoNote ? 'done' : undefined}
              data-note-id={note.id}
              tabIndex={-1}
            >
                <div className="note-meta">
                  <span className="note-author">{authorName}</span>
                  <span className="note-time">{moment(note.createdAt).fromNow()}</span>
                  {isTodoNote ? (
                    <span className={`badge ${isDone ? 'text-bg-success' : 'text-bg-warning'}`}>
                      {isDone ? 'Done' : 'TODO'}
                    </span>
                  ) : null}
                  {isDone && note?.todoCompletedAt ? (
                    <span className="note-done-meta">
                      Done {moment(note.todoCompletedAt).fromNow()}{doneBy ? ` by ${doneBy}` : ''}
                    </span>
                  ) : null}
                </div>
                <div className="note-body">{displayBody}</div>
              {canManage ? (
                <div className="note-actions">
                  {!isTodoNote || isDone ? (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      style={{ textDecoration: 'none' }}
                      onClick={() => handleConvertToTodo(note)}
                      disabled={actionPendingId === note.id}
                    >
                      {isTodoNote && isDone ? 'Reopen TODO' : 'Mark TODO'}
                    </button>
                  ) : null}
                  {isTodoNote && !isDone ? (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                        style={{ textDecoration: 'none' }}
                        onClick={() => handleMarkDone(note)}
                        disabled={actionPendingId === note.id}
                      >
                        Mark done
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0"
                      style={{ textDecoration: 'none' }}
                      onClick={() => handleDelete(note)}
                      disabled={actionPendingId === note.id}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {showScrollCueUp ? (
          <>
            <div className="note-scroll-fade note-scroll-fade-top" aria-hidden="true" />
            <div className="note-scroll-cue note-scroll-cue-up" aria-hidden="true" />
          </>
        ) : null}
        {showScrollCueDown ? (
          <>
            <div className="note-scroll-fade note-scroll-fade-bottom" aria-hidden="true" />
            <div className="note-scroll-cue note-scroll-cue-down" aria-hidden="true" />
          </>
        ) : null}
      </div>
      {canManage ? (
        <form className="note-form" onSubmit={handleSubmit}>
          <textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Add an internal note…"
            maxLength={NOTE_MAX_LENGTH}
            rows={3}
            disabled={submitting}
          />
          <div className="note-form-actions">
            <label className="form-check-label">
              <input
                type="checkbox"
                className="form-check-input"
                checked={isTodo}
                onChange={(event) => setIsTodo(event.target.checked)}
                disabled={submitting}
              />
              {' '}Mark as TODO
            </label>
            <button type="submit" className="btn btn-sm btn-outline-dark" disabled={submitting}>
              {submitting ? 'Saving…' : 'Add note'}
            </button>
          </div>
          {error ? <div className="alert alert-danger note-error">{error}</div> : null}
        </form>
      ) : null}
      <div className="note-visibility-hint">
        These notes are only visible to your organization’s editors.
      </div>
    </div>
  );
};

export default GuideNotes;
